import { RichTextDisplay } from "@tellescope/ui";
import type { ChatMessage } from "@tellescope/types-models";
import type { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { formatDateTime, formatName } from "@tellescope/utils";
import { useOrganization } from "../../../shared/store/organizationStore";
import { AttachmentIndicator } from "./AttachmentIndicator";
import { MessageSenderAvatar } from "./MessageSenderAvatar";
import { OrganizationAvatar } from "./OrganizationAvatar";

interface MessageBubbleProps {
  message: ChatMessage;
  isOwnMessage: boolean;
  sender?: TellescopeModelFromName<"users">;
}

export const MessageBubble = ({ message, isOwnMessage, sender }: MessageBubbleProps) => {
  const organization = useOrganization();

  // Care-team messages always show sender identity: the user's own avatar/name
  // when their portalSettings opt in, otherwise the organization's as a default.
  const isFromCareTeam = !isOwnMessage && sender !== undefined;
  const showUserAvatar = isFromCareTeam && sender.portalSettings?.showAvatar === true;
  const showOrgAvatar = isFromCareTeam && !showUserAvatar && organization !== null;
  const displayName = isFromCareTeam
    ? sender.portalSettings?.showDisplayName === true
      ? formatName(sender)
      : organization?.name
    : undefined;

  return (
    <div className={`flex ${isOwnMessage ? "justify-end" : "justify-start"} gap-2 mb-3`}>
      {showUserAvatar && sender && <MessageSenderAvatar user={sender} />}
      {showOrgAvatar && organization && <OrganizationAvatar organization={organization} />}
      <div className="flex flex-col items-start max-w-[80%]">
        {displayName && <p className="text-xs text-muted-foreground mb-0.5 px-1">{displayName}</p>}
        <div
          className={`rounded-2xl px-4 py-2 ${
            isOwnMessage ? "bg-primary text-primary-foreground rounded-br-md" : "bg-muted rounded-bl-md"
          }`}
        >
          {message.html ? (
            <RichTextDisplay key={message.id} content={message.html} className="text-sm" />
          ) : (
            <p className="text-sm whitespace-pre-wrap">{message.message}</p>
          )}

          {message.attachments && message.attachments.length > 0 && (
            <AttachmentIndicator count={message.attachments.length} />
          )}

          <p className={`text-xs mt-1 ${isOwnMessage ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
            {formatDateTime(message.timestamp || message.updatedAt, "MMM D, YYYY at hh:mm a", true)}
          </p>
        </div>
      </div>
    </div>
  );
};
